import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  createWwwAdminBlogPost,
  deleteWwwAdminBlogPost,
  fetchWwwAdminBlogPosts,
  updateWwwAdminBlogPost,
  type CreateWwwAdminBlogPostInput,
  type UpdateWwwAdminBlogPostInput,
  type WwwAdminBlogPost,
  type WwwBlogStatus,
} from '../../api/adminWwwBlog';

const QUERY_KEY = ['admin-www-blog'];
const formatTimestamp = (value?: string | null) => {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  return parsed.toLocaleString();
};

type BlogFormState = {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  coverImageUrl: string;
  tags: string;
  status: WwwBlogStatus;
};

const DEFAULT_FORM: BlogFormState = {
  slug: '',
  title: '',
  excerpt: '',
  content: '',
  coverImageUrl: '',
  tags: '',
  status: 'draft',
};

const statusBadgeClasses: Record<WwwBlogStatus, string> = {
  draft: 'border-white/20 bg-white/10 text-gray-200',
  published: 'border-emerald-400/40 bg-emerald-500/20 text-emerald-200',
  archived: 'border-amber-400/40 bg-amber-500/20 text-amber-100',
};

function parseTags(value: string) {
  return value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function buildPayload(form: BlogFormState): CreateWwwAdminBlogPostInput {
  return {
    slug: form.slug.trim(),
    title: form.title.trim(),
    excerpt: form.excerpt.trim() || undefined,
    content: form.content.trim(),
    coverImageUrl: form.coverImageUrl.trim() || undefined,
    tags: parseTags(form.tags),
    status: form.status,
  };
}

function mapBlogToForm(post: WwwAdminBlogPost): BlogFormState {
  return {
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt ?? '',
    content: post.content,
    coverImageUrl: post.coverImageUrl ?? '',
    tags: (post.tags ?? []).join(', '),
    status: post.status,
  };
}

export function WwwBlogAdmin() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<BlogFormState>(DEFAULT_FORM);

  const postsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchWwwAdminBlogPosts,
    staleTime: 1000 * 15,
  });

  const createMutation = useMutation({
    mutationFn: createWwwAdminBlogPost,
    onSuccess: () => {
      toast.success('WWW blog post created.');
      setForm(DEFAULT_FORM);
      setEditingId(null);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Unable to create blog post.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWwwAdminBlogPostInput }) =>
      updateWwwAdminBlogPost(id, payload),
    onSuccess: () => {
      toast.success('WWW blog post updated.');
      setForm(DEFAULT_FORM);
      setEditingId(null);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Unable to update blog post.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteWwwAdminBlogPost,
    onSuccess: () => {
      toast.success('WWW blog post deleted.');
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Unable to delete blog post.');
    },
  });

  const sortedPosts = useMemo(
    () => [...(postsQuery.data ?? [])].sort((a, b) => (a.updatedAt ?? '').localeCompare(b.updatedAt ?? '') * -1),
    [postsQuery.data],
  );

  const saving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = () => {
    const payload = buildPayload(form);
    if (!payload.slug || !payload.title || !payload.content) {
      toast.error('Please fill in slug, title, and content.');
      return;
    }

    if (editingId) {
      updateMutation.mutate({ id: editingId, payload });
      return;
    }
    createMutation.mutate(payload);
  };

  return (
    <main className="min-h-screen bg-[#05060f] pb-24" aria-labelledby="www-blog-admin-title">
      <div className="mx-auto max-w-6xl space-y-8 px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-[#f7ef8a]">Admin · WWW</p>
            <h1 id="www-blog-admin-title" className="mt-2 text-3xl font-semibold text-white">
              Website Blog
            </h1>
            <p className="text-sm text-gray-400">Create, edit, publish, and archive blog content for the marketing website.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              onClick={() => postsQuery.refetch()}
              disabled={postsQuery.isFetching}
            >
              {postsQuery.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Refresh
            </Button>
            <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/10">
              <Link to="/admin/www/jobs">Manage WWW Jobs</Link>
            </Button>
          </div>
        </div>

        <Card className="border-white/10 bg-white/[0.03]">
          <CardHeader>
            <CardTitle className="text-white">{editingId ? 'Edit blog post' : 'Create blog post'}</CardTitle>
            <CardDescription className="text-gray-400">
              Set status to `published` when ready to show the post publicly.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-white" htmlFor="www-blog-title">
                  Title
                </Label>
                <Input
                  id="www-blog-title"
                  value={form.title}
                  onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white" htmlFor="www-blog-slug">
                  Slug
                </Label>
                <Input
                  id="www-blog-slug"
                  value={form.slug}
                  onChange={(event) => setForm((current) => ({ ...current, slug: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white" htmlFor="www-blog-excerpt">
                Excerpt
              </Label>
              <Textarea
                id="www-blog-excerpt"
                rows={3}
                value={form.excerpt}
                onChange={(event) => setForm((current) => ({ ...current, excerpt: event.target.value }))}
                className="border-white/20 bg-white/[0.06] text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white" htmlFor="www-blog-content">
                Content
              </Label>
              <Textarea
                id="www-blog-content"
                rows={12}
                value={form.content}
                onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))}
                className="border-white/20 bg-white/[0.06] text-white"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2 md:col-span-2">
                <Label className="text-white" htmlFor="www-blog-tags">
                  Tags (comma separated)
                </Label>
                <Input
                  id="www-blog-tags"
                  value={form.tags}
                  onChange={(event) => setForm((current) => ({ ...current, tags: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Status</Label>
                <Select value={form.status} onValueChange={(value) => setForm((current) => ({ ...current, status: value as WwwBlogStatus }))}>
                  <SelectTrigger className="border-white/20 bg-white/[0.06] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="role-surface-admin border-white/10 bg-role-admin-overlay text-white backdrop-blur-xl">
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white" htmlFor="www-blog-cover-url">
                Cover image URL (optional)
              </Label>
              <Input
                id="www-blog-cover-url"
                value={form.coverImageUrl}
                onChange={(event) => setForm((current) => ({ ...current, coverImageUrl: event.target.value }))}
                className="border-white/20 bg-white/[0.06] text-white"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={onSubmit}
                className="bg-gold-gradient text-black hover:brightness-110"
                disabled={saving}
              >
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                {editingId ? 'Save Changes' : 'Create Post'}
              </Button>
              {editingId && (
                <Button
                  type="button"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  onClick={() => {
                    setEditingId(null);
                    setForm(DEFAULT_FORM);
                  }}
                >
                  Cancel edit
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-white/[0.03]">
          <CardHeader>
            <CardTitle className="text-white">Existing blog posts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {postsQuery.isLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading blog posts…
              </div>
            )}
            {postsQuery.isError && (
              <p className="text-sm text-red-300">
                {postsQuery.error instanceof Error ? postsQuery.error.message : 'Unable to load blog posts.'}
              </p>
            )}
            {!postsQuery.isLoading && !postsQuery.isError && sortedPosts.length === 0 && (
              <p className="text-sm text-gray-400">No posts yet.</p>
            )}
            {sortedPosts.map((post) => (
              <Card key={post.id} className="border-white/10 bg-white/[0.02]">
                <CardContent className="flex flex-col gap-4 p-5 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-base font-semibold text-white">{post.title}</p>
                      <Badge className={statusBadgeClasses[post.status]}>{post.status}</Badge>
                    </div>
                    <p className="text-sm text-gray-400">/blog/{post.slug}</p>
                    {post.excerpt ? <p className="text-sm text-gray-300">{post.excerpt}</p> : null}
                    <p className="text-xs text-gray-500">Updated: {formatTimestamp(post.updatedAt ?? post.createdAt)}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                      onClick={() => {
                        setEditingId(post.id);
                        setForm(mapBlogToForm(post));
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                      onClick={() =>
                        updateMutation.mutate({
                          id: post.id,
                          payload: { status: post.status === 'published' ? 'draft' : 'published' },
                        })
                      }
                      disabled={updateMutation.isPending}
                    >
                      {post.status === 'published' ? 'Unpublish' : 'Publish'}
                    </Button>
                    <Button
                      variant="ghost"
                      className="text-red-300 hover:text-red-200"
                      onClick={() => {
                        const confirmed = window.confirm(`Delete blog post "${post.title}"? This cannot be undone.`);
                        if (confirmed) {
                          deleteMutation.mutate(post.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
